import { useCallback, useEffect, useRef, useState } from "react";

import { CommonKVNode } from "../../../types";
import { listItems, nodeEditText } from "./kvStoreNodes";
import { KVNodeEdit } from "./kvStoreSeed";

// At most one node edits at a time, identified by its full delimiter-joined
// key. Its own key segment and its value open together in one editor.
export type NodeEditing = {
  key: string;
  segment: string;
  originalSegment: string;
  text: string;
  originalText: string;
  // Whether the text applies as a list, one item per line, rather than as
  // the string it is.
  isList: boolean;
  originalIsList: boolean;
  // Which field takes focus when the editor opens: the column that was
  // clicked.
  focus: "key" | "value";
};

// The node editor's lifecycle: open on one node, track its segment and
// text, apply on a click outside or a Tab out, close.
const useNodeEditing = (editNode: (key: string, edit: KVNodeEdit) => void) => {
  const [editing, setEditing] = useState<NodeEditing | null>(null);
  const keyInputRef = useRef<HTMLInputElement>(null);
  const valueEditorRef = useRef<HTMLDivElement>(null);

  const startEditing = (
    key: string,
    segment: string,
    node: CommonKVNode,
    focus: "key" | "value"
  ) => {
    // A structural node's value starts empty — the edit adds its first
    // value, typed as plain text like any new value. A list opens one item
    // per line.
    const isList = Array.isArray(node.value);
    const text = nodeEditText(node) ?? "";
    setEditing({
      key,
      segment,
      focus,
      originalSegment: segment,
      text,
      originalText: text,
      isList,
      originalIsList: isList,
    });
  };

  // Applies whatever changed in the open editor — the segment rename and
  // the value edit land together as one apply.
  const applyEditing = useCallback(() => {
    if (!editing) {
      return;
    }
    const edit: KVNodeEdit = {};
    const segment = editing.segment.trim();
    if (segment && segment !== editing.originalSegment) {
      edit.segment = segment;
    }
    if (
      editing.text !== editing.originalText ||
      editing.isList !== editing.originalIsList
    ) {
      edit.value = editing.isList ? listItems(editing.text) : editing.text;
    }
    setEditing(null);
    if ("segment" in edit || "value" in edit) {
      editNode(editing.key, edit);
    }
  }, [editing, editNode]);

  useEffect(() => {
    if (!editing) {
      return;
    }
    const handleMouseDown = (event: MouseEvent) => {
      if (
        keyInputRef.current?.contains(event.target as Node) ||
        valueEditorRef.current?.contains(event.target as Node)
      ) {
        return;
      }
      applyEditing();
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [editing, applyEditing]);

  return {
    editing,
    keyInputRef,
    valueEditorRef,
    startEditing,
    changeEditing: (
      patch: Partial<Pick<NodeEditing, "segment" | "text" | "isList">>
    ) => setEditing((current) => current && { ...current, ...patch }),
    applyEditing,
  };
};

export type NodeEditor = ReturnType<typeof useNodeEditing>;

export default useNodeEditing;
